import { cleanJourneyName, completedJourneyName, formatWindowEnd } from "./premiumLiteJourneyBanner";

export { completedJourneyName, JOURNEY_COMPLETED } from "./premiumLiteJourneyBanner";

/**
 * The first step of a new Premium Lite (client, 23/10/2026): choose the
 * nutritional journey. Metabolico is the default, and the member has 72 hours
 * to change it; the one active at the end becomes the Premium Lite journey.
 *
 * `premiumLite` is the page-header block (GET master-club-nutritional-goals/
 * page-header): { main_protocol_name, window_open, window_ends_at, … }.
 */

export const DEFAULT_JOURNEY_NAME = "Metabolico";

const STORAGE_PREFIX = "ds_lite_journey_intro";
const WELCOME_PREFIX = "ds_lite_welcome_at";
const WINDOW_MS = 72 * 60 * 60 * 1000;

const journeyName = (premiumLite) =>
  cleanJourneyName(premiumLite?.main_protocol_name) || DEFAULT_JOURNEY_NAME;

const windowEnd = (premiumLite) =>
  premiumLite?.window_open ? formatWindowEnd(premiumLite.window_ends_at) : null;

/** The welcome popup's line for a new Premium Lite member. */
export function liteWelcomeCallout(premiumLite) {
  const name = journeyName(premiumLite);
  const until = windowEnd(premiumLite);
  const time = until ? `72 ore (fino a ${until})` : "3 giorni";

  return `Il primo passo è scegliere il tuo percorso nutrizionale. Partiamo da ${name}: hai ${time} per cambiarlo e scegliere quello definitivo.`;
}

/** The reminder popup while the 72 hours are open; null when they aren't. */
export function journeyIntroCopy(premiumLite) {
  const until = windowEnd(premiumLite);
  if (!until) return null;

  // A Speciale / Stagionale journey ended: Metabolico runs meanwhile.
  const completed = completedJourneyName(premiumLite);
  if (completed) {
    return {
      title: `Hai completato ${completed}`,
      body: `Ora scegli il percorso con cui continuare. Hai 72 ore, fino a ${until}; nel frattempo il percorso attivo è ${journeyName(premiumLite)}.`,
    };
  }

  return {
    title: "Scegli il tuo percorso Premium Lite",
    body: `Il percorso attivo è ${journeyName(premiumLite)}. Hai 72 ore, fino a ${until}, per cambiarlo e scegliere quello definitivo.`,
  };
}

/** One reminder per member and per 72-hour window (a new Lite period opens a new one). */
export const introStorageKey = (userId, windowEndsAt) =>
  `${STORAGE_PREFIX}:${userId}:${windowEndsAt}`;

const read = (key) => {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};

const write = (key, value) => {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* ignore: private browsing or storage disabled */
  }
};

export const markIntroShown = (userId, windowEndsAt) => {
  if (userId && windowEndsAt) write(introStorageKey(userId, windowEndsAt), "1");
};

/**
 * The welcome popup already said it: remember when, so the reminder doesn't
 * follow it for the same window.
 */
export const markLiteWelcomeShown = (userId, now = Date.now()) => {
  if (userId) write(`${WELCOME_PREFIX}:${userId}`, String(now));
};

const welcomeCoversWindow = (userId, windowEndsAt) => {
  const shownAt = Number(read(`${WELCOME_PREFIX}:${userId}`));
  const end = new Date(windowEndsAt).getTime();
  if (!Number.isFinite(shownAt) || shownAt <= 0 || Number.isNaN(end)) return false;

  // Shown since this window started (a minute of slack for clock differences).
  return shownAt >= end - WINDOW_MS - 60 * 1000;
};

// Sign-in, onboarding and the purchase screens: never interrupt them.
const QUIET_PATHS = [
  "/login",
  "/o/",
  "/verifica-email",
  "/impersonate",
  "/impostazioni-iniziali",
  "/onboarding",
  "/paywall",
];

export const isJourneysPage = (pathname) =>
  String(pathname || "").startsWith("/preferenze-nutrizionali");

/**
 * "show" — open the reminder now;
 * "seen" — the member already has the message for this window (the welcome
 *          popup said it, or they are on the journeys page, whose banner says
 *          the same): remember it and don't show;
 * "wait" — not now (not Lite, window closed or unknown, a quiet screen).
 */
export function journeyIntroDecision({ isPremiumLite, userId, premiumLite, pathname = "" }) {
  if (!isPremiumLite || !userId || !journeyIntroCopy(premiumLite)) return "wait";
  if (read(introStorageKey(userId, premiumLite.window_ends_at))) return "wait";
  // The welcome popup is about a new plan, not a completed journey.
  if (!completedJourneyName(premiumLite) && welcomeCoversWindow(userId, premiumLite.window_ends_at)) return "seen";
  if (isJourneysPage(pathname)) return "seen";

  const path = String(pathname || "");
  if (path === "/" || QUIET_PATHS.some((quiet) => path.startsWith(quiet))) return "wait";

  return "show";
}
